import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { HttpService } from './http-service.service';
import { IOperatingHourModel } from '../models/operating-hour.model';
import {
  ApiData,
  ApiPaginationResponse,
  ApiResponse,
} from '../common/api-response.types';

interface OperatingHourApiEntity {
  id: number;
  branchId?: number;
  branch_id?: number;
  weekday: string;
  open_time: string;
  close_time: string;
  coreId?: number;
  core_id?: number;
  deletedAt?: string | null;
}

export interface IAvailableTimeSlot {
  id: number;
  timeFrom: string;
  timeto: string;
  date: string;
}

export interface IAvailableTimeSlotsPayload {
  timezone: string;
  serverDateTime: string;
  serverDate: string;
  serverTime: string;
  isOutOfHours: boolean;
  quoterEnabled: boolean;
  operationalState: string;
  exceedsStock: boolean;
  requiresManualApproval: boolean;
  collectionDate: string | null;
  slots: IAvailableTimeSlot[];
}

@Injectable({
  providedIn: 'root',
})
export class OperatingHoursService {
  private readonly operatingHoursEndpoint = '/branch';

  constructor(private readonly httpService: HttpService) {}

  public getOperatingHours(
    branchId: number
  ): Observable<IOperatingHourModel[]> {
    return this.httpService
      .get<ApiResponse<OperatingHourApiEntity>>(
        `${this.operatingHoursEndpoint}/${branchId}/operating-hours`
      )
      .pipe(
        map((response: ApiResponse<OperatingHourApiEntity>) => {
          const operatingHours = this.mapApiResponse(response);
          return operatingHours;
        })
      );
  }

  public getAvailableTimeSlots(
    branchId: number,
    days: number = 1,
    inventory?: { currencyCode?: string; requestedAmount?: number },
  ): Observable<IAvailableTimeSlotsPayload> {
    const params = new URLSearchParams({ days: String(days) });
    if (inventory?.currencyCode && (inventory.requestedAmount ?? 0) > 0) {
      params.set('currencyCode', inventory.currencyCode);
      params.set('requestedAmount', String(inventory.requestedAmount));
    }
    return this.httpService
      .get<ApiResponse<any>>(
        `${this.operatingHoursEndpoint}/${branchId}/operating-hours/available-slots?${params.toString()}`
      )
      .pipe(map((response) => this.mapAvailableSlotsResponse(response)));
  }

  private mapApiResponse(
    response: ApiResponse<OperatingHourApiEntity>
  ): IOperatingHourModel[] {
    if (!response?.data) {
      return [];
    }

    const payload = this.extractDataArray(response.data);
    return payload.map((hour) => this.mapToOperatingHourModel(hour));
  }

  private extractDataArray(
    data: ApiData<OperatingHourApiEntity>
  ): OperatingHourApiEntity[] {
    if (Array.isArray(data)) {
      return data;
    }

    if (this.isPaginationResponse(data)) {
      if (Array.isArray(data.items)) {
        return data.items;
      }
      return data.items ? [data.items] : [];
    }

    return data ? [data] : [];
  }

  private mapToOperatingHourModel(
    hour: OperatingHourApiEntity
  ): IOperatingHourModel {
    return {
      id: hour.id,
      branchId: hour.branchId ?? hour.branch_id ?? 0,
      weekday: hour.weekday as any,
      open_time: hour.open_time ?? '',
      close_time: hour.close_time ?? '',
      coreId: hour.coreId ?? hour.core_id,
      deletedAt: hour.deletedAt ? new Date(hour.deletedAt) : null,
    };
  }

  private isPaginationResponse<T>(
    data: ApiData<T>
  ): data is ApiPaginationResponse<T> {
    if (data === null || typeof data !== 'object') {
      return false;
    }

    return Object.hasOwn(data as Record<string, unknown>, 'items');
  }

  private mapAvailableSlotsResponse(
    response: ApiResponse<any>
  ): IAvailableTimeSlotsPayload {
    const responseData = response?.data as
      | ApiData<IAvailableTimeSlotsPayload>
      | undefined;
    const payload = responseData
      ? this.extractAvailableSlotsData(responseData)
      : undefined;

    return {
      timezone: payload?.timezone ?? 'America/Mexico_City',
      serverDateTime: payload?.serverDateTime ?? '',
      serverDate: payload?.serverDate ?? '',
      serverTime: payload?.serverTime ?? '',
      isOutOfHours: Boolean(payload?.isOutOfHours),
      quoterEnabled: payload?.quoterEnabled !== false,
      operationalState: payload?.operationalState ?? '',
      exceedsStock: Boolean(payload?.exceedsStock),
      requiresManualApproval: Boolean(payload?.requiresManualApproval),
      collectionDate: payload?.collectionDate ?? null,
      slots: Array.isArray(payload?.slots) ? payload.slots : [],
    };
  }

  private extractAvailableSlotsData(
    data: ApiData<IAvailableTimeSlotsPayload>
  ): IAvailableTimeSlotsPayload | undefined {
    if (!data) {
      return undefined;
    }

    if (Array.isArray(data)) {
      return data[0];
    }

    if (this.isPaginationResponse(data)) {
      if (Array.isArray(data.items)) {
        return data.items[0];
      }
      return data.items;
    }

    return data;
  }
}
